// Snapshots / Preview page (A/S only).
//
// Dry-run for a single period. Shows the table of accounts that would freeze,
// inline warnings (missing balance, stale Tiller), totals, and the Freeze
// button at the bottom. Confirmation pops before the freeze actually lands.

import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { snapshots as snapshotsApi, ApiError } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { formatBalance } from '@/lib/format';
import type {
  SnapshotFreezeResponse,
  SnapshotPreviewItem,
  SnapshotPreviewResponse,
} from '@frt/shared-types';

export function SnapshotPreviewPage() {
  const { data: me } = useMe();
  const { period } = useParams({ from: '/snapshots/$period/preview' as never }) as { period: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = useState(false);

  const canFreeze = me && (me.role === 'admin' || me.role === 'senior');

  const previewQuery = useQuery({
    queryKey: ['snapshots', period, 'preview'] as const,
    queryFn: () => snapshotsApi.preview(period),
    staleTime: 5_000,
  });

  const freezeMut = useMutation({
    mutationFn: () => snapshotsApi.freeze(period),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['snapshots'] });
      await navigate({ to: '/snapshots/$period', params: { period } });
    },
  });

  if (!canFreeze) {
    return (
      <div className="text-sm text-slate-600">
        Period preview requires admin or senior role.
      </div>
    );
  }

  if (previewQuery.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (previewQuery.error) {
    return (
      <div className="text-sm text-red-600">
        Failed to load preview: {(previewQuery.error as Error).message}
      </div>
    );
  }
  const data = previewQuery.data as SnapshotPreviewResponse;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Freeze preview — <span className="font-mono">{period}</span>
          </h1>
          <p className="text-sm text-slate-500">
            Cutoff: <span className="font-mono">{data.cutoff_at}</span>
          </p>
        </div>
        {data.any_already_frozen && (
          <span className="rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800">
            Period already locked — additional rows would error
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Accounts" value={data.totals.accounts_total} />
        <Stat label="Already frozen" value={data.totals.accounts_already_frozen} />
        <Stat
          label="Missing balance"
          value={data.totals.accounts_missing_balance}
          tone={data.totals.accounts_missing_balance > 0 ? 'warn' : 'ok'}
        />
        <Stat
          label="Stale Tiller"
          value={data.totals.accounts_stale_tiller}
          tone={data.totals.accounts_stale_tiller > 0 ? 'warn' : 'ok'}
        />
        <Stat
          label="Sum balance"
          value={formatBalance(data.totals.balance_amount_sum)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Accounts in this freeze</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-2 pr-3">Account</th>
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Tester</th>
                  <th className="py-2 pr-3">Tiller</th>
                  <th className="py-2 pr-3 text-right">Balance</th>
                  <th className="py-2 pr-3">As of</th>
                  <th className="py-2 pr-3">Warnings</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item: SnapshotPreviewItem) => (
                  <tr key={item.account_id} className="border-t border-slate-100">
                    <td className="py-2 pr-3 font-mono text-xs">{item.display_name}</td>
                    <td className="py-2 pr-3">{item.frt_customer_code}</td>
                    <td className="py-2 pr-3">{item.tester_display}</td>
                    <td className="py-2 pr-3">{item.tiller_status}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {formatBalance(item.balance_amount)}
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{item.balance_as_of ?? '—'}</td>
                    <td className="py-2 pr-3 text-xs">
                      {item.already_frozen && (
                        <Badge tone="info">already frozen</Badge>
                      )}
                      {item.missing_balance && <Badge tone="warn">missing balance</Badge>}
                      {item.stale_tiller && <Badge tone="warn">stale tiller</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <div className="flex justify-end gap-2">
        {showConfirm ? (
          <>
            <span className="self-center text-sm text-slate-700">
              Confirm freeze for <span className="font-mono">{period}</span>?
            </span>
            <Button
              variant="secondary"
              onClick={() => setShowConfirm(false)}
              disabled={freezeMut.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => freezeMut.mutate()}
              disabled={freezeMut.isPending || data.any_already_frozen}
            >
              {freezeMut.isPending ? 'Freezing…' : 'Confirm freeze'}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={data.any_already_frozen}
          >
            Freeze period
          </Button>
        )}
      </div>

      {freezeMut.error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Freeze failed:{' '}
          {freezeMut.error instanceof ApiError
            ? `${freezeMut.error.code} — ${freezeMut.error.message}`
            : (freezeMut.error as Error).message}
        </div>
      )}

      {freezeMut.data && (
        <FreezeResultBanner result={freezeMut.data as SnapshotFreezeResponse} />
      )}
    </div>
  );
}

function FreezeResultBanner({ result }: { result: SnapshotFreezeResponse }) {
  return (
    <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
      Froze {result.frozen_count} snapshot(s) for {result.period}
      {result.missing_balance_count > 0
        ? ` (${result.missing_balance_count} written with missing balance)`
        : ''}
      .
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'ok',
}: {
  label: string;
  value: number | string;
  tone?: 'ok' | 'warn';
}) {
  const cls = tone === 'warn' ? 'text-amber-800' : 'text-slate-900';
  return (
    <div className="rounded border border-slate-200 bg-white p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function Badge({ tone, children }: { tone: 'info' | 'warn'; children: React.ReactNode }) {
  const cls =
    tone === 'warn'
      ? 'bg-amber-100 text-amber-800'
      : 'bg-slate-100 text-slate-700';
  return (
    <span className={`mr-1 inline-block rounded px-1.5 py-0.5 ${cls}`}>{children}</span>
  );
}
